import React, {
  FC,
  useEffect,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { styled } from "@mui/system";

import { COLORS, SPACINGS } from "../common/consts";
import { DbOrganizationTrialDetails } from "../types/organizations";
import { Errors } from "../types/errors";
import { setupProspectWebhook, signUserOut } from "../services/firebase";
import { signedInUserAtom, organizationAtom, hubspotPageAtom } from "../state/atoms";
import Tabs from "../components/Display/Tabs";
import Button, { ButtonVariants } from "../components/Display/Button";
import FormHelper, { Severity } from "../components/DataInput/FormHelper";
import Text, { TextVariant } from "../components/Display/Text";
import { BlockBone, ChartSkeleton, StatBones, TextBone } from "../components/Display/Skeletons";
import Animation from "../components/Display/Animation";
import Dialog from "../components/Display/Dialog";
import StatusBar from "../components/Display/StatusBar";
import Card from "../components/Display/Card";

import Insights from "./Insights/Insights";
import Settings from "./Settings";
import CallerIdTab from "../components/CallerId/CallerIdTab";
import Users from "./Users/Users";
import CheckoutDialog from "../components/Billing/CheckoutDialog";
import ConnectOutreach from "./Outreach/ConnectOutreach";
import ConnectHubSpot from "./HubSpot/ConnectHubSpot";
import { useConnectOutreach } from "../common/hooks/useConnectOutreach";
import { useConnectHubSpot } from "../common/hooks/useConnectHubSpot";
import { useCrm } from "../components/CrmUi/useCrm";
import { OuBone } from "../components/CrmUi";
import { HubSpotSettingsLayout, HubSpotSideNav } from "../components/Layout/HubSpotAppShell";
import JobChanges, { JobChangesNavBadge, JobChangesTabLabel } from "./JobChanges/JobChanges";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const StyledValue = styled("span")`
  color: var(--color-integrationDisconnected, ${COLORS.integrationDisconnected});
`;

interface TrialExpiry {
  isTrialExpired: boolean;
  relativeTime: string | null;
}

const calculateTrialExpiry = (
  trialDetails?: DbOrganizationTrialDetails
): TrialExpiry => {
  const currentTimestamp = dayjs();
  const expiresAt = trialDetails?.expiresAt && dayjs(trialDetails.expiresAt);
  const isTrialExpired = !!(expiresAt && currentTimestamp.isAfter(expiresAt));
  const relativeTime =
    (!isTrialExpired && expiresAt && expiresAt.fromNow(true)) || null;

  return {
    isTrialExpired,
    relativeTime,
  };
};

const getTrialMessage = (
  trialExpiry: TrialExpiry,
  trialDetails?: DbOrganizationTrialDetails
): ReactNode => {
  // Card-required trial (converts to a paid subscription automatically).
  if (trialDetails?.verificationsPerUser !== undefined) {
    if (trialExpiry.relativeTime) {
      return (
        <Text variant={TextVariant.sm} className="text-center">
          Your free trial ends in{" "}
          <StyledValue>{trialExpiry.relativeTime}</StyledValue> — up to{" "}
          <StyledValue>
            {trialDetails.verificationsPerUser.toLocaleString()}
          </StyledValue>{" "}
          verifications per user.
          <br />
          Your subscription starts automatically when the trial ends.
        </Text>
      );
    }
    return (
      <Text variant={TextVariant.sm} className="text-center">
        Your free trial has ended
      </Text>
    );
  }

  // Legacy no-card trial (org-wide pool of verifications).
  if (trialExpiry.relativeTime && trialDetails?.remainingVerifications) {
    return (
      <Text variant={TextVariant.sm} className="text-center">
        You have <StyledValue>{trialExpiry.relativeTime}</StyledValue> to use
        the remaining
        <br />
        <StyledValue>{trialDetails.remainingVerifications}</StyledValue>{" "}
        verifications in your boring trial
      </Text>
    );
  }

  if (trialDetails) {
    return (
      <Text variant={TextVariant.sm} className="text-center">
        {!trialExpiry.relativeTime
          ? "Your boring trial has expired"
          : "You have used all your trial verifications"}
      </Text>
    );
  }

  // Trial not started yet: a card is required to activate it.
  return (
    <Text variant={TextVariant.sm} className="text-center">
      Try boring free for <StyledValue>2 weeks</StyledValue> — up to{" "}
      <StyledValue>5 users</StyledValue> and{" "}
      <StyledValue>1,000</StyledValue> verifications per user.
      <br />
      Add a card to start your trial. You won&apos;t be charged until it ends,
      and you can cancel anytime.
    </Text>
  );
};

/**
 * HubSpot orgs' dashboard before the organization loads: HubSpot's navigation
 * column and a canvas of report cards, not Outreach's tab strip.
 */
/* Nothing to open until the organization loads. */
const ignoreNavigation = (): void => undefined;

const HubSpotDashboardSkeleton: FC = () => {
  const page = useRecoilValue(hubspotPageAtom);
  return (
    <div className="hs-app" aria-busy="true">
      <HubSpotSideNav page={page} onNavigate={ignoreNavigation} showUsers={false} />
      <div className="hs-app__main">
        <main className="hs-canvas" aria-label="Loading">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <OuBone variant="rounded" width={240} height={28} />
            <div style={{ display: "flex", gap: 8 }}>
              <OuBone variant="rounded" width={88} height={32} />
              <OuBone variant="rounded" width={96} height={32} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="hs-report-card" style={{ minHeight: 220 }}>
                <OuBone width="45%" height={18} />
                <OuBone variant="rounded" width={72} height={20} />
                <OuBone variant="rounded" height={120} style={{ marginTop: 16 }} />
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

/**
 * The dashboard before the organization lands in Recoil: the tab strip, the
 * Insights header card, and the stats-and-chart panel it opens on (HubSpot
 * orgs: HubSpotDashboardSkeleton).
 */
export const DashboardSkeleton: FC = () => (useCrm() === "hubspot" ? <HubSpotDashboardSkeleton /> : <OutreachDashboardSkeleton />);

const OutreachDashboardSkeleton: FC = () => (
      <div className="p-0">
    <div className="d-flex" style={{ gap: SPACINGS.xs, marginLeft: SPACINGS.sm }}>
      {[80, 64, 88, 88, 80].map((width, i) => (
        <BlockBone key={i} width={width} height={40} radius="0.375rem 0.375rem 0 0" />
      ))}
    </div>
    <div className="d-flex flex-column gap-4 mt-3">
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-top">
          <div style={{ flex: "1 1 auto" }}>
            <TextBone width={120} size="1.25rem" />
            <TextBone width="62%" size="0.875rem" />
          </div>
          <BlockBone width={180} height={40} />
        </div>
      </Card>
      <Card className="p-0 d-flex">
        <div style={{ minWidth: "15rem", maxWidth: "19rem", padding: SPACINGS.xl }}>
          <StatBones valueWidth={104} labelWidth={140} />
          <div className="mt-4">
            <StatBones valueWidth={72} labelWidth={96} />
          </div>
          <div className="mt-4">
            <StatBones valueWidth={72} labelWidth={112} />
          </div>
        </div>
        <div className="flex-grow-1 py-2" style={{ borderLeft: `1px solid var(--color-borderPrimary, ${COLORS.borderPrimary})` }}>
          <ChartSkeleton />
        </div>
      </Card>
    </div>
  </div>
);

const Dashboard: FC = () => {
  // Call hooks unconditionally
  const signedInUser = useRecoilValue(signedInUserAtom);
  const organization = useRecoilValue(organizationAtom);
  const [isProcessingWebhook, setIsProcessingWebhook] = useState(false);
  const [showSetupWebhookButton, setShowSetupWebhookButton] = useState(true);
  const [webhookError, setWebhookError] = useState("");
  const [isTrialDialogOpen, setIsTrialDialogOpen] = useState(true);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isOAuthProcessing, setIsOAuthProcessing] = useState(() => {
    return localStorage.getItem("oauthCodeProcessing") === "true";
  });

  // Call OAuth hooks to process any pending codes
  // These hooks will process OAuth codes if they exist in localStorage
  // The hooks themselves check for codes before making unnecessary API calls
  useConnectOutreach();
  useConnectHubSpot();
  const crm = useCrm();
  const [hubspotPage, setHubspotPage] = useRecoilState(hubspotPageAtom);
  // ?open=billing (the upgrade links in the extension, the HubSpot app and
  // the MCP): open on Billing once, then drop the parameter so a reload does not.
  const [openBilling] = useState(() => new URLSearchParams(window.location.search).get("open") === "billing");
  useEffect(() => {
    if (!openBilling) return;
    if (crm === "hubspot") setHubspotPage("billing");
    const url = new URL(window.location.href);
    url.searchParams.delete("open");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, [openBilling, crm, setHubspotPage]);

  const {
    organizationId,
    isProspectWebhookCreated,
    trialDetails,
    isTrial,
    isPayingCustomer,
    isSegmentedAccount,
    isOutreachAuthorized: userIsOutreachAuthorized,
    isHubSpotAuthorized: userIsHubSpotAuthorized,
  } = signedInUser || {};

  // Check auth status from both user and organization (org is source of truth)
  const isOutreachAuthorized = userIsOutreachAuthorized || organization?.isOutreachAuthorized || false;
  const isHubSpotAuthorized = userIsHubSpotAuthorized || organization?.isHubSpotAuthorized || false;

  const trialExpiry = useMemo(
    () => calculateTrialExpiry(trialDetails),
    [trialDetails]
  );

  const handleSetupWebhook = useCallback(async () => {
    setIsProcessingWebhook(true);
    try {
      const result = await setupProspectWebhook();
      if (result.status === 200) {
        setShowSetupWebhookButton(false);
      }
    } catch (error) {
      setWebhookError(
        `Error setting up webhook (Code ${Errors.CONNECT_OUTREACH_SET_WEBHOOK_FUNCTION_ERROR}).`
      );
    } finally {
      setIsProcessingWebhook(false);
    }
  }, []);

  const handleUpgrade = useCallback(() => {
    setIsTrialDialogOpen(false);
    setIsCheckoutOpen(true);
  }, []);

  useEffect(() => {
    if (isProspectWebhookCreated) {
      setShowSetupWebhookButton(false);
    }
  }, [isProspectWebhookCreated]);

  const renderWebhookSetup = () => (
    <Animation className="col-12">
      <div className="d-flex h-100 align-items-center justify-content-center m-auto">
        <div className="w-75 w-lg-400px">
          <Text variant={TextVariant.h4} className="mb-3 text-center">
            Connect to your Outreach account
          </Text>
          <div className="mb-3">
            <Button
              fullWidth
              variant={ButtonVariants.brand}
              type="submit"
              disabled={isProcessingWebhook}
              onClick={handleSetupWebhook}
              loading={isProcessingWebhook}
              startIcon={<i className="fa-solid fa-plug" />}
            >
              Setup webhook
            </Button>
          </div>
          <Text variant={TextVariant.sm} className="text-center">
            I will continue later,{" "}
            <span onClick={signUserOut} className="btn-link cursor-pointer">
              sign out
            </span>{" "}
            for now.
          </Text>
          <FormHelper helperText={webhookError} severity={Severity.error} />
        </div>
      </div>
    </Animation>
  );

  // Card-required trial that's still running: informational only — the
  // subscription converts automatically, so there's nothing to upgrade.
  const isActiveCardTrial =
    trialDetails?.verificationsPerUser !== undefined &&
    !trialExpiry.isTrialExpired;

  const renderDialog = (): ReactNode => (
    <Dialog
      open={isTrialDialogOpen}
      primaryButtonProps={
        isActiveCardTrial
          ? {
              onClick: () => setIsTrialDialogOpen(false),
              children: "Got it",
              variant: ButtonVariants.brand,
            }
          : {
              onClick: handleUpgrade,
              children: trialDetails ? "Upgrade" : "Start free trial",
              variant: ButtonVariants.brand,
            }
      }
      onClose={() => setIsTrialDialogOpen(false)}
    >
      {getTrialMessage(trialExpiry, trialDetails)}
    </Dialog>
  );

  // Check auth status - organization is source of truth
  // Use isOutreachAuthorized/isHubSpotAuthorized flags (they exist in Firestore)
  // Also check auth objects as fallback
  const orgIsOutreachAuthed = organization?.isOutreachAuthorized === true || !!organization?.outreachAuthentication;
  const orgIsHubSpotAuthed = organization?.isHubSpotAuthorized === true || !!organization?.hubSpotAuthentication;
  const isAnyIntegrationAuthorized = isOutreachAuthorized || isHubSpotAuthorized || orgIsOutreachAuthed || orgIsHubSpotAuthed;
  
  // Check if OAuth is processing and show loading state
  useEffect(() => {
    const checkOAuthProcessing = () => {
      const oauthProcessing = localStorage.getItem("oauthCodeProcessing") === "true";
      const processingTime = localStorage.getItem("oauthCodeProcessingTime");
      
      // Clear stuck processing flag (older than 10 seconds)
      if (oauthProcessing && processingTime) {
        const elapsed = Date.now() - parseInt(processingTime, 10);
        if (elapsed > 10000) {
          console.warn("⚠️ OAuth processing flag stuck for >10s, clearing it");
          localStorage.removeItem("oauthCodeProcessing");
          localStorage.removeItem("oauthCodeProcessingTime");
          setIsOAuthProcessing(false);
          return;
        }
      }
      
      setIsOAuthProcessing(oauthProcessing);
      
      if (!oauthProcessing && isAnyIntegrationAuthorized) {
        localStorage.removeItem("oauthCodeProcessing");
        localStorage.removeItem("oauthCodeProcessingTime");
        localStorage.removeItem("oauthCodeFailed");
        localStorage.removeItem("oauthCodeFailedAt");
      }
    };
    
    checkOAuthProcessing();
    // Check periodically while processing
    const interval = setInterval(checkOAuthProcessing, 500);
    
    return () => clearInterval(interval);
  }, [isAnyIntegrationAuthorized]);
  
  // NOTE: Removed OAuth redirect logic from Dashboard.
  // OAuth redirect happens in Signup.tsx (for new users) and Settings.tsx (for reconnecting).
  // Dashboard just shows loading until App.tsx populates org data in Recoil.
  
  // If already authed, skip all connect UI and show dashboard - no loops
  // HubSpot orgs: HubSpot's own app layout, with the navigation column in
  // place of the tab strip and the settings panel around Users and Settings.
  if (isAnyIntegrationAuthorized && crm === "hubspot") {
    const showUsers = !!isSegmentedAccount;
    // Extension's status now lives on the Integration page.
    const page = hubspotPage === "users" && !showUsers ? "account" : hubspotPage === "extension" ? "integration-status" : hubspotPage;
    return (
      <div className="hs-app">
        <HubSpotSideNav page={page} onNavigate={setHubspotPage} showUsers={showUsers} jobChangesBadge={<JobChangesNavBadge />} />
        <div className="hs-app__main">
          {page === "insights" ? (
            <Insights organizationId={organizationId} />
          ) : page === "job-changes" ? (
            <JobChanges />
          ) : (
            <HubSpotSettingsLayout page={page} onNavigate={setHubspotPage} showUsers={showUsers}>
              {page === "users" ? <Users /> : <Settings hubspotSection={page} />}
            </HubSpotSettingsLayout>
          )}
        </div>
        {!isPayingCustomer &&
          (isTrial || organization?.isTrialExpired) &&
          renderDialog()}
        <CheckoutDialog
          open={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
        />
      </div>
    );
  }

  if (isAnyIntegrationAuthorized) {
    return (
      <div className="p-0">
        <Tabs
          initialValue={openBilling ? "settings" : undefined}
          options={[
            {
              label: "Insights",
              value: "insights",
              children: <Insights organizationId={organizationId} />,
            },
            ...(isSegmentedAccount
              ? [
                  {
                    label: "Users",
                    value: "users",
                    children: <Users />,
                  },
                ]
              : []),
            {
              label: <JobChangesTabLabel />,
              value: "job-changes",
              children: <JobChanges />,
            },
            // Caller ID scores calls placed through Outreach; HubSpot orgs have none to score.
            ...(crm === "hubspot"
              ? []
              : [
                  {
                    label: "Caller ID",
                    value: "caller-id",
                    children: <CallerIdTab />,
                  },
                ]),
            {
              label: "Settings",
              value: "settings",
              children: <Settings initialSection={openBilling ? "billing" : undefined} />,
            },
          ]}
        />
        {!isPayingCustomer &&
          (isTrial || organization?.isTrialExpired) &&
          renderDialog()}
        <CheckoutDialog
          open={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
        />
      </div>
    );
  }

  // Show status bar only during OAuth processing (onboarding).
  // Underneath it, the dashboard it is about to become: the tab strip, the
  // Insights header card, and the stats-and-chart panel.
  return (
    <>
      {isOAuthProcessing && <StatusBar />}
      <DashboardSkeleton />
    </>
  );
};

export default Dashboard;
